import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';

/**
 * Motion hooks that are safe for prerendered (hydrated) pages: the first render is
 * identical on server and client and shows final content; animation starts after mount.
 */

/** True when animations should be skipped (reduced-motion users, or no matchMedia, e.g. tests/SSR). */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

const subscribeMotionPref = (onChange: () => void) => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
  const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
};

/**
 * Whether decorative motion may run. Always false on the server and during hydration
 * (so the first client render matches the prerendered HTML), then the real preference.
 */
export function useMotionAllowed(): boolean {
  return useSyncExternalStore(
    subscribeMotionPref,
    () => !prefersReducedMotion(),
    () => false,
  );
}

const isInViewport = (el: Element) => {
  const r = el.getBoundingClientRect();
  return r.top < window.innerHeight && r.bottom > 0;
};

/**
 * `visible` becomes true once the element scrolls into view (once). Elements already on
 * screen when the page loads are marked visible immediately (before paint), so prerendered
 * content never flashes out and back in.
 */
export function useInView<T extends Element>(
  options: IntersectionObserverInit = { threshold: 0.15 },
) {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined' || prefersReducedMotion() || isInViewport(el)) {
      // Measured before paint: content already on screen must not be hidden and
      // re-revealed after hydration.
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    if (visible || !ref.current || typeof IntersectionObserver === 'undefined') return;
    const el = ref.current;
    const io = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) {
        setVisible(true);
        io.disconnect();
      }
    }, options);
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- observe once
  }, [visible]);
  return { ref, visible };
}

/**
 * Shows `target` immediately (so prerendered HTML has the real number). When `start`
 * first becomes true it counts up from 0; later target changes animate from the
 * currently shown value. Reduced-motion users just see the number.
 */
export function useCountUp(target: number, { durationMs = 1200, start = true } = {}): number {
  const [value, setValue] = useState(target);
  const current = useRef(target);
  const hasRun = useRef(false);

  useEffect(() => {
    if (!start) return;
    if (prefersReducedMotion()) {
      const id = requestAnimationFrame(() => {
        current.current = target;
        setValue(target);
      });
      return () => cancelAnimationFrame(id);
    }
    const from = hasRun.current ? current.current : 0;
    hasRun.current = true;
    let raf = 0;
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / durationMs);
      const next = Math.round(from + (target - from) * (1 - Math.pow(1 - p, 3)));
      current.current = next;
      setValue(next);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs, start]);
  return value;
}

/** Marks the document as "JS ready" so scroll-reveal styles apply only after hydration. */
export function useMotionReady() {
  useLayoutEffect(() => {
    document.documentElement.classList.add('motion-ready');
  }, []);
}
